// src/pages/YourTrips.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import { format, isToday, isAfter, isSameDay } from "date-fns";
import { MessageCircle } from "lucide-react";

// 🔗 API + Socket base
const RAW_API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.bookyourneed.com/api";
const API_BASE = RAW_API_BASE;
const API_HOST = RAW_API_BASE.replace(/\/api\/?$/, "");

const socket = io(API_BASE, { transports: ["websocket"] });

// ✅ Safe date formatting
const safeFormat = (dateStr, fmt = "PPPpp") => {
  if (!dateStr) return "Unknown";
  try {
    const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(d.getTime())) return "Unknown";
    return format(d, fmt);
  } catch {
    return "Unknown";
  }
};

// ✅ Get a safe Date object from ride.date
const getRideDate = (ride) => {
  if (!ride?.date) return null;
  const d = new Date(ride.date);
  if (isNaN(d.getTime())) return null;
  return d;
};

// ✅ Normalize booking status
const getStatus = (req) => {
  return (
    req.requestStatus ||
    req.status ||
    req.rideStatus ||
    req.rideId?.status ||
    null
  );
};

const YourTrips = () => {
  const navigate = useNavigate();
  const customerId = localStorage.getItem("customerId");

  const [requests, setRequests] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal for complete / dispute
  const [modal, setModal] = useState({
    open: false,
    type: null, // "complete" | "dispute"
    request: null,
  });

  // Redirect if not logged in
  useEffect(() => {
    if (!customerId) {
      navigate("/login", { replace: true });
    }
  }, [customerId, navigate]);

  // Fetch rides + chats
  useEffect(() => {
    if (!customerId) return;

    const fetchAll = async () => {
      try {
        const [reqRes, chatRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ride-booking/my-requests/${customerId}`),
          axios.get(`${API_BASE}/api/ride-chat/my-chats/${customerId}`),
        ]);

        const raw = reqRes.data || [];
        const now = new Date();

        // Filter requests
        const requestsData = raw.filter((r) => {
          // hide archived rides
          if (r.rideId?.isArchived) return false;

          const status = getStatus(r);
          const refunded =
            status === "refunded" || r.rideStatus === "refunded";

          // ❌ Cancelled by driver → show only 48h then hide
          if (r.requestStatus === "cancelled_by_driver") {
            const updated = new Date(r.updatedAt || r.createdAt);
            const ageHours = (now - updated) / (1000 * 60 * 60);
            return ageHours <= 48;
          }

          // 💸 Refunded → keep 72h
          if (refunded) {
            const updated = new Date(r.updatedAt || r.createdAt);
            const ageHours = (now - updated) / (1000 * 60 * 60);
            return ageHours <= 72;
          }

          // ✅ Completed & paid out → keep 7 days
          if (r.rideId?.status === "completed" && r.rideId?.isPaidOut) {
            const updated = new Date(r.updatedAt || r.createdAt);
            const ageDays = (now - updated) / (1000 * 60 * 60 * 24);
            return ageDays <= 7;
          }

          return true;
        });

        // Chats already formatted by backend:
        // { _id, rideId, worker: {name, profilePhotoUrl}, lastMessage, lastMessageAt }
        let chatsData = chatRes.data || [];

        // Hide chat cards for rides that already have an active booking card
        const bookedRideIds = new Set(
          requestsData.map((r) => String(r.rideId?._id || r.rideId))
        );
        chatsData = chatsData.filter(
          (c) => !bookedRideIds.has(String(c.rideId))
        );

        setRequests(requestsData);
        setChats(chatsData);
      } catch (err) {
        console.error("❌ Fetch error (YourTrips):", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [customerId]);

  // Socket: join ride-customer room & live chat updates
  useEffect(() => {
    if (!customerId) return;

    // register customer for ride events
    socket.emit("registerRideCustomer", customerId);
    console.log("👤 Joined ride socket room for customer:", customerId);

    const handleRideMessage = (data) => {
      console.log("💬 New ride message received:", data);
      setChats((prevChats) => {
        const idx = prevChats.findIndex(
          (c) => String(c.rideId) === String(data.rideId)
        );
        if (idx >= 0) {
          const updated = [...prevChats];
          updated[idx] = {
            ...updated[idx],
            lastMessage: data.message?.text,
            lastMessageAt: data.message?.timestamp,
            worker: {
              ...(updated[idx].worker || {}),
              _id: data.workerId || updated[idx].worker?._id,
              name:
                data.message?.driverName ||
                updated[idx].worker?.name ||
                "Driver",
              profilePhotoUrl:
                data.message?.driverPhoto ||
                updated[idx].worker?.profilePhotoUrl ||
                null,
            },
          };
          return updated;
        } else {
          // first time chat card
          return [
            ...prevChats,
            {
              _id: data.chatId || undefined,
              rideId: data.rideId,
              worker: {
                _id: data.workerId,
                name: data.message?.driverName || "Driver",
                profilePhotoUrl: data.message?.driverPhoto || null,
              },
              lastMessage: data.message?.text,
              lastMessageAt: data.message?.timestamp,
            },
          ];
        }
      });
    };

    const handleRideChatUpdate = (update) => {
      console.log("🔔 Ride chat update:", update);
      setChats((prev) =>
        prev.map((c) =>
          String(c.rideId) === String(update.rideId)
            ? {
                ...c,
                lastMessage: update.lastMessage,
                lastMessageAt: update.timestamp,
              }
            : c
        )
      );
    };

    socket.on("ride-message", handleRideMessage);
    socket.on("ride-chat-update", handleRideChatUpdate);

    return () => {
      socket.off("ride-message", handleRideMessage);
      socket.off("ride-chat-update", handleRideChatUpdate);
    };
  }, [customerId]);

  // Confirm ride completion (per booking)
  const confirmComplete = async (req) => {
  try {
    const bookingId = req._id || req.bookingId || req.requestId || req.id;

    if (!bookingId) {
      console.error("NO BOOKING ID FOUND", req);
      alert("Booking ID missing. Cannot complete.");
      return;
    }

    await axios.post(`${API_BASE}/api/ridepayment/customer-complete`, {
      bookingId,
      customerId,
    });

    window.location.reload();
  } catch (err) {
    console.error("❌ customer-complete error:", err.response?.data || err);
    alert("Could NOT complete ride.");
  }
};

  // ✅ core: Dispute ride
  const confirmDispute = async (bookingId) => {
    try {
      await axios.post(`${API_BASE}/api/ridepayment/dispute`, {
        bookingId,
        reason: "Customer reported an issue with this ride",
        raisedBy: "customer",
      });
      setModal({ open: false, type: null, request: null });
      window.location.reload();
    } catch (err) {
      console.error("❌ Dispute error:", err);
      alert("❌ Could not raise dispute. Please try again.");
    }
  };

  // ✅ logic for showing completion + dispute buttons
  const showCompletionButtons = (rideDate, ride, req) => {
    if (!rideDate || ride.isPaidOut) return false;
    const now = new Date();
    const rideDayOrAfter =
      isToday(rideDate) || isAfter(now, rideDate) || isSameDay(now, rideDate);

    const status = getStatus(req);

    return rideDayOrAfter && (status === "accepted" || status === "worker_completed");
  };

  // 🧠 Categorize requests into Today / Upcoming / Completed / Cancelled / Disputed
  const now = new Date();
  const sections = {
    today: [],
    upcoming: [],
    completed: [],
    cancelled: [],
    disputed: [],
  };

  requests.forEach((req) => {
    const ride = req.rideId || {};
    const status = getStatus(req);
    const rideDate = getRideDate(ride);

    const refunded =
      status === "refunded" ||
      req.rideStatus === "refunded" ||
      req.requestStatus === "refunded";

    const cancelledByDriver = req.requestStatus === "cancelled_by_driver";
    const cancelledGeneric =
      status === "cancelled" || status === "declined" || cancelledByDriver;

    const disputed =
      status === "disputed" ||
      req.rideStatus === "disputed" ||
      req.requestStatus === "disputed";

    const completed =
      status === "completed" ||
      ride.status === "completed" ||
      ride.isPaidOut ||
      req.rideStatus === "worker_completed";

    if (disputed) {
      sections.disputed.push(req);
    } else if (cancelledGeneric || refunded) {
      sections.cancelled.push(req);
    } else if (completed) {
      sections.completed.push(req);
    } else if (rideDate && isToday(rideDate)) {
      sections.today.push(req);
    } else {
      sections.upcoming.push(req);
    }
  });

  // 🔁 renderer for a single booking card (reused in all sections)
  const renderRequestCard = (req) => {
    const ride = req.rideId || {};
    const driver = ride.workerId || {};

    const rideIdClean = ride._id || req.rideId;
    const status = getStatus(req);

    const refunded =
      status === "refunded" || req.requestStatus === "cancelled_by_driver";

    const rideDate = getRideDate(ride);

    const driverPhoto = driver.profilePhotoUrl
      ? `${API_HOST}${driver.profilePhotoUrl}`
      : "/default-avatar.png";

    return (
      <div
        key={req._id}
        className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5 md:p-6 rounded-3xl shadow-xl border border-indigo-100 hover:shadow-blue-200 hover:-translate-y-0.5 transition-transform"
      >
        {/* 💬 Chat Button */}
        {!refunded && (
  <button
    onClick={() =>
      navigate(`/customer/ride-chat/${rideIdClean}`, {
        state: {
          from: req.segmentFrom,
          to: req.segmentTo,
          price: req.segmentPrice,
          isStop: !!req.segmentFrom,
        },
      })
    }
    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full
               bg-gradient-to-br from-white to-blue-50 shadow-md border border-blue-100
               hover:shadow-lg hover:scale-105 transition-transform duration-150"
    title="Chat with driver"
  >
    <MessageCircle className="w-4 h-4 text-blue-600" />
  </button>
)}   {/* <-- THIS WAS MISSING */}

        {/* 🌟 Banner for today's rides */}
        {rideDate && isToday(rideDate) && !refunded && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-[10px] md:text-xs font-semibold px-4 py-1.5 rounded-full shadow-md animate-pulse">
            🚗 Your Ride is Today!
          </div>
        )}

        {/* Driver Info */}
        <div className="flex items-center gap-4 mb-3 mt-4">
          <img
            src={driverPhoto}
            alt="Driver"
            className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover border-2 border-blue-200 shadow-sm"
          />
          <div className="min-w-0">
            <p className="font-bold text-gray-900 text-base md:text-lg truncate">
              {driver.name || "Driver"}
            </p>
            <p className="text-xs md:text-sm text-gray-600 truncate">
              {req.segmentFrom || ride.from} → {req.segmentTo || ride.to}
            </p>
          </div>
        </div>

        <p className="text-[11px] md:text-xs text-gray-400 mb-4">
          {rideDate ? safeFormat(rideDate, "MMMM d, yyyy") : "N/A"} at{" "}
          {ride.time || "N/A"}
        </p>

        {/* Status + Price */}
        <div className="flex items-center justify-between mb-2 gap-2">
         <p className="text-indigo-600 font-bold text-lg md:text-xl">
  $
  {
    req.segmentPrice !== undefined && req.segmentPrice !== null
      ? Number(req.segmentPrice).toFixed(2)
      : ride.pricePerSeat
      ? Number(ride.pricePerSeat).toFixed(2)
      : "N/A"
  }
</p>

          <div className="flex flex-wrap gap-1 justify-end">
            {ride.isPaidOut && (
              <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-[11px] md:text-xs shadow-sm">
                💰 Payment Released
              </span>
            )}
            {(status === "accepted" || status === "confirmed") &&
              !ride.isPaidOut && (
                <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-[11px] md:text-xs shadow-sm">
                  ✅ Accepted
                </span>
              )}
            {(status === "pending" || status === "active") && (
              <span className="px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold text-[11px] md:text-xs shadow-sm">
                ⏳ Pending
              </span>
            )}
            {status === "declined" && (
              <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold text-[11px] md:text-xs shadow-sm">
                ❌ Declined
              </span>
            )}
            {status === "disputed" && (
              <span className="px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold text-[11px] md:text-xs shadow-sm">
                ⚠️ Disputed
              </span>
            )}
            {(status === "completed" ||
              ride.status === "completed" ||
              ride.isPaidOut ||
              req.rideStatus === "worker_completed") && (
              <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold text-[11px] md:text-xs shadow-sm">
                ✅ Completed
              </span>
            )}
            {refunded && (
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold text-[11px] md:text-xs shadow-sm">
                💸 Refunded — Driver Cancelled
              </span>
            )}
          </div>
        </div>

        {refunded && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs md:text-sm text-yellow-800 font-medium shadow-sm">
            The driver cancelled this ride. Your payment has been refunded.
            We’re sorry for the inconvenience 💛
          </div>
        )}

       {/* COMPLETION LOGIC (FINAL FIX) */}
      {!refunded &&
  (() => {
    const status = getStatus(req);

    // ⭐ CASE 1: Customer already confirmed
    if (status === "completed" || ride.status === "completed") {
      return (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
          <p className="text-blue-700 font-semibold text-sm">
            🎉 You confirmed this ride.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Payment has been released to the driver.
          </p>
        </div>
      );
    }

    // ⭐ CASE 2: Driver marked complete → customer must confirm
    if (status === "worker_completed") {
      return (
        <div className="flex flex-col gap-3 mt-4">
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800 shadow-sm">
            The driver marked this ride complete. Please confirm or report an issue within 48 hours.
          </div>

          <button
            onClick={() =>
              setModal({
                open: true,
                type: "complete",
                request: req,
              })
            }
            className="bg-green-600 text-white py-2 rounded-xl shadow hover:bg-green-700 transition-all font-semibold text-sm"
          >
            ✅ Confirm Ride Completion
          </button>

          <button
            onClick={() =>
              setModal({
                open: true,
                type: "dispute",
                request: req,
              })
            }
            className="bg-red-500 text-white py-2 rounded-xl shadow hover:bg-red-600 transition-all font-semibold text-sm"
          >
            ⚠️ Report an Issue
          </button>
        </div>
      );
    }

    // ⭐ CASE 3: Ride is today or later AND accepted → allow complete
    if (showCompletionButtons(rideDate, ride, req)) {
      return (
        <div className="flex flex-col gap-3 mt-4">
          <button
            onClick={() =>
              setModal({
                open: true,
                type: "complete",
                request: req,
              })
            }
            className="bg-green-600 text-white py-2 rounded-xl shadow hover:bg-green-700 transition-all font-semibold text-sm"
          >
            ✅ Confirm Ride Completion
          </button>

          <button
            onClick={() =>
              setModal({
                open: true,
                type: "dispute",
                request: req,
              })
            }
            className="bg-red-500 text-white py-2 rounded-xl shadow hover:bg-red-600 transition-all font-semibold text-sm"
          >
            ⚠️ Report an Issue
          </button>
        </div>
      );
    }

    return null;
  })()}
  </div>
    );
  };
  return (
    <>
      {/* MAIN PAGE */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-6 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 drop-shadow-sm">
            Your Trips & Messages
          </h2>

          {/* SECTIONS FOR REQUESTS */}
          <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800">
            Your Ride Requests
          </h3>

          {requests.length === 0 ? (
            <p className="text-gray-500 mb-10 text-sm md:text-base">
              You haven’t requested any rides yet.
            </p>
          ) : (
            <div className="space-y-10 mb-12">
              {/* Today */}
              {sections.today.length > 0 && (
                <div>
                  <h4 className="text-lg md:text-xl font-semibold mb-3 text-gray-800">
                    Today
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                    {sections.today.map(renderRequestCard)}
                  </div>
                </div>
              )}

              {/* Upcoming */}
              {sections.upcoming.length > 0 && (
                <div>
                  <h4 className="text-lg md:text-xl font-semibold mb-3 text-gray-800">
                    Upcoming
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                    {sections.upcoming.map(renderRequestCard)}
                  </div>
                </div>
              )}

              {/* Completed */}
              {sections.completed.length > 0 && (
                <div>
                  <h4 className="text-lg md:text-xl font-semibold mb-3 text-gray-800">
                    Completed (last 7 days)
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                    {sections.completed.map(renderRequestCard)}
                  </div>
                </div>
              )}

              {/* Cancelled / Refunded */}
              {sections.cancelled.length > 0 && (
                <div>
                  <h4 className="text-lg md:text-xl font-semibold mb-3 text-gray-800">
                    Cancelled & Refunded
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                    {sections.cancelled.map(renderRequestCard)}
                  </div>
                </div>
              )}

              {/* Disputed */}
              {sections.disputed.length > 0 && (
                <div>
                  <h4 className="text-lg md:text-xl font-semibold mb-3 text-gray-800">
                    Disputed
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                    {sections.disputed.map(renderRequestCard)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 💬 Chats Section */}
          <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800">
            Your Messages
          </h3>
          {chats.length === 0 ? (
            <p className="text-gray-500 text-sm md:text-base">No chats yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {chats.map((chat) => {
                const worker = chat.worker || {};
                const workerPhoto = worker.profilePhotoUrl
                  ? `${API_HOST}${worker.profilePhotoUrl}`
                  : "/default-avatar.png";

                const lastMessageText =
                  chat.lastMessage && chat.lastMessage.trim().length > 0
                    ? chat.lastMessage
                    : "No messages yet";

                const lastTime = chat.lastMessageAt || null;

                return (
                  <div
                    key={chat._id || chat.rideId}
                    onClick={() =>
  navigate(`/customer/ride-chat/${chat.rideId?._id || chat.rideId}`, {
    state: {
      from: chat.segmentFrom || null,
      to: chat.segmentTo || null,
      price: chat.segmentPrice || null,
      isStop: !!chat.segmentFrom,
    },
  })
}


                    className="relative bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-5 md:p-6 rounded-3xl shadow-xl
                               border border-indigo-100 hover:shadow-indigo-200 hover:-translate-y-0.5 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <img
                        src={workerPhoto}
                        alt="Driver"
                        className="w-12 h-12 rounded-full object-cover border-2 border-indigo-200 shadow-sm"
                      />

                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-lg truncate">
                          {worker.name || "Driver"}
                        </p>

                        <p className="text-sm text-gray-600 truncate">
                          {lastMessageText}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-400 mb-4">
                      Last active {safeFormat(lastTime)}
                    </p>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/customer/ride-chat/${chat.rideId?._id || chat.rideId}`
                        );
                      }}
                      className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full 
                                 bg-white shadow border border-indigo-200 hover:scale-105"
                    >
                      <MessageCircle className="w-4 h-4 text-indigo-700" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

             {/* NICE MODAL for confirm / dispute */}
      {modal.open && modal.request && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-900">
              {modal.type === "complete"
                ? "Confirm Ride Completion"
                : "Report an Issue"}
            </h3>

            <p className="text-sm text-gray-600 mb-6">
              {modal.type === "complete"
                ? "Are you sure this ride was completed successfully? Once you confirm, payment will be released to the driver."
                : "Are you sure you want to report an issue with this ride? Our support team will review and hold the payment until it is resolved."}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() =>
                  setModal({ open: false, type: null, request: null })
                }
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>

              {modal.type === "complete" ? (
                <button
                  onClick={() => confirmComplete(modal.request)}
                  className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 shadow"
                >
                  Confirm
                </button>
              ) : (
                <button
                  onClick={() => confirmDispute(modal.request._id)}
                  className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 shadow"
                >
                  Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default YourTrips;
